import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ComponentType, type MouseEvent, type ReactNode, useState } from 'react';
import { ChainLogo } from './icons/ChainLogo';
import { FoldersIcon } from './icons/FoldersIcon';
import { HomeIcon } from './icons/HomeIcon';
import { SettingsIcon } from './icons/SettingsIcon';
import { SignalsIcon } from './icons/SignalsIcon';
import { WandIcon } from './icons/WandIcon';
import { WorkflowIcon } from './icons/WorkflowIcon';
import { AccountMenu } from './shell/AccountMenu';
import type { AccountControl } from './shell/types';

export type SidebarDestination =
  | 'dashboard'
  | 'workflows'
  | 'reports'
  | 'explore'
  | 'signals'
  | 'outcomes'
  | 'studio'
  | 'batches'
  | 'settings';

export type SidebarChild = 'creatives' | 'elements' | 'competitors' | 'performance' | 'media';

export interface NavChild {
  id: SidebarChild;
  label: string;
  description: string;
  Icon: ComponentType<SvgIconProps>;
}

interface NavEntry {
  id: SidebarDestination;
  label: string;
  Icon: ComponentType<SvgIconProps>;
}

const PRIMARY_NAV: NavEntry[] = [
  { id: 'dashboard', label: 'Home', Icon: HomeIcon },
  { id: 'workflows', label: 'Workflows', Icon: WorkflowIcon },
  { id: 'reports', label: 'Reports', Icon: AssessmentOutlinedIcon },
  { id: 'explore', label: 'Explore', Icon: ExploreOutlinedIcon },
  { id: 'signals', label: 'Signals', Icon: SignalsIcon },
  { id: 'outcomes', label: 'Outcomes', Icon: FoldersIcon },
  { id: 'studio', label: 'Studio', Icon: WandIcon },
  { id: 'batches', label: 'Batches', Icon: ViewListOutlinedIcon },
];

const SUBMENUS: Partial<Record<SidebarDestination, NavChild[]>> = {
  explore: [
    {
      id: 'creatives',
      label: 'Creatives',
      description: 'Every ad ranked by KPI',
      Icon: PlayCircleOutlineOutlinedIcon,
    },
    {
      id: 'elements',
      label: 'Elements',
      description: "What's driving results",
      Icon: CategoryOutlinedIcon,
    },
    {
      id: 'competitors',
      label: 'Competitors',
      description: 'The category landscape',
      Icon: EmojiEventsOutlinedIcon,
    },
  ],
  reports: [
    {
      id: 'performance',
      label: 'Performance',
      description: 'Period over period review',
      Icon: AssessmentOutlinedIcon,
    },
    {
      id: 'media',
      label: 'Media',
      description: 'How your media is performing',
      Icon: PermMediaOutlinedIcon,
    },
  ],
};

interface NavButtonProps {
  label: string;
  Icon: ComponentType<SvgIconProps>;
  active: boolean;
  badge?: number | undefined;
  testId: string;
  onClick: (event: MouseEvent<HTMLElement>) => void;
}

function NavButton({ label, Icon, active, badge, testId, onClick }: NavButtonProps): ReactNode {
  return (
    <ButtonBase
      onClick={onClick}
      data-test={testId}
      aria-current={active ? 'page' : undefined}
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: 56,
        flexDirection: 'column',
        gap: 1,
        py: 1,
        borderRadius: 1,
        color: active ? 'common.white' : 'text.secondary',
        bgcolor: active ? 'background.icon' : 'transparent',
        transition: 'background-color .15s, color .15s',
        '&:hover': {
          bgcolor: active ? 'background.icon' : 'background.platform',
          color: active ? 'common.white' : 'text.primary',
        },
      }}
    >
      {badge ? (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 12,
            minWidth: 16,
            height: 16,
            px: 1,
            borderRadius: 1,
            bgcolor: 'error.main',
            color: 'common.white',
            border: '1.5px solid',
            borderColor: 'common.white',
            fontSize: 9,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {badge}
        </Box>
      ) : null}
      <Icon sx={{ fontSize: 19 }} />
      <Typography variant="nav">{label}</Typography>
    </ButtonBase>
  );
}

interface NavChildRowProps {
  child: NavChild;
  active: boolean;
  onClick: () => void;
}

function NavChildRow({ child, active, onClick }: NavChildRowProps): ReactNode {
  const { label, description, Icon } = child;
  return (
    <MenuItem
      onClick={onClick}
      data-test={`nav-child-${child.id}`}
      sx={{
        gap: 2,
        py: 1,
        px: 2,
        borderRadius: 1,
        bgcolor: 'transparent',
        color: 'text.primary',
        '&:hover': {
          bgcolor: 'background.platform',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 1,
          bgcolor: active ? 'background.icon' : 'background.platform',
          color: active ? 'common.white' : 'text.secondary',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6">{label}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>
      </Box>
    </MenuItem>
  );
}

interface NavEntryProps {
  entry: NavEntry;
  active: boolean;
  activeChild: SidebarChild | undefined;
  badge?: number | undefined;
  onNavigate: () => void;
  onNavigateChild: (child: SidebarChild) => void;
}

function NavEntryButton({
  entry,
  active,
  activeChild,
  badge,
  onNavigate,
  onNavigateChild,
}: NavEntryProps): ReactNode {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const children = SUBMENUS[entry.id];

  if (!children) {
    return (
      <NavButton
        label={entry.label}
        Icon={entry.Icon}
        active={active}
        badge={badge}
        testId={`nav-${entry.id}`}
        onClick={onNavigate}
      />
    );
  }

  return (
    <>
      <NavButton
        label={entry.label}
        Icon={entry.Icon}
        active={active}
        badge={badge}
        testId={`nav-${entry.id}`}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { ml: 1, minWidth: 260, boxShadow: 5 } },
          list: { sx: { p: 1 } },
        }}
      >
        <Typography variant="overline" sx={{ color: 'text.secondary', px: 2, py: 1 }}>
          {entry.label}
        </Typography>
        {children.map((child) => (
          <NavChildRow
            key={child.id}
            child={child}
            active={activeChild === child.id}
            onClick={() => {
              setAnchorEl(null);
              onNavigateChild(child.id);
            }}
          />
        ))}
      </Menu>
    </>
  );
}

interface SidebarProps {
  active: SidebarDestination;
  onNavigate: (destination: SidebarDestination) => void;
  activeChild?: SidebarChild;
  onNavigateChild: (child: SidebarChild) => void;
  account: AccountControl;
  showAccount: boolean;
  signalCount?: number;
  visible?: Partial<Record<SidebarDestination, boolean>>;
  /** Top mark. Defaults to the Alison glyph. */
  logo?: ReactNode;
  logoTitle?: string;
  /** Rich hover for the mark; the plain title serves when absent. */
  logoTooltip?: ReactNode;
}

export function Sidebar({
  active,
  onNavigate,
  activeChild,
  onNavigateChild,
  account,
  showAccount,
  signalCount = 0,
  visible,
  logo,
  logoTitle = 'Chain by Alison',
  logoTooltip,
}: SidebarProps): ReactNode {
  const theme = useTheme();
  const isVisible = (destination: SidebarDestination): boolean => visible?.[destination] !== false;
  return (
    <Box
      component="aside"
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 60,
        width: theme.layout.sidebarWidth,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 1,
        py: 2,
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Tooltip title={logoTooltip ?? ''} placement="right">
        <ButtonBase
          onClick={() => onNavigate('dashboard')}
          aria-label={logoTitle}
          {...(logoTooltip === undefined ? { title: logoTitle } : {})}
          sx={{ width: '100%', height: 50, borderRadius: 1 }}
        >
          {logo ?? <ChainLogo sx={{ fontSize: 30 }} />}
        </ButtonBase>
      </Tooltip>

      <Box
        component="nav"
        data-tour="nav"
        sx={{
          width: '100%',
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {PRIMARY_NAV.filter((entry) => isVisible(entry.id)).map((entry) => (
          <NavEntryButton
            key={entry.id}
            entry={entry}
            active={active === entry.id}
            activeChild={activeChild}
            badge={entry.id === 'signals' ? signalCount : undefined}
            onNavigate={() => onNavigate(entry.id)}
            onNavigateChild={onNavigateChild}
          />
        ))}
      </Box>

      <Box sx={{ flex: 1 }} />

      {isVisible('settings') ? (
        <NavButton
          label="Settings"
          Icon={SettingsIcon}
          active={active === 'settings'}
          testId="nav-settings"
          onClick={() => onNavigate('settings')}
        />
      ) : null}

      {showAccount ? (
        <Box sx={{ mt: 1 }}>
          <AccountMenu
            account={account}
            onSettings={account.onSettings}
            onLogout={account.onLogout}
          />
        </Box>
      ) : null}
    </Box>
  );
}
